/**
 * The physical layout of the record database: which object stores exist, how
 * each one is keyed, and which materialized index fields it carries.
 *
 * `STORE_IDS` in `@ff/domain` is the authority for *what stores exist* — a new
 * store is a change to that constant, never an ad-hoc `createObjectStore` call
 * that the constant does not know about. This file only says how each of those
 * stores is shaped, and {@link RECORD_STORE_LAYOUTS} is asserted to cover
 * `STORE_IDS` exactly, so the two cannot drift.
 *
 * Two decisions here are structural rather than incidental:
 *
 *  - **The three singleton stores are keyed out-of-line on one fixed literal.**
 *    There is no `keyPath`, and every write uses the same constant, so the
 *    store cannot physically hold two active checkpoints. Single-effort
 *    authority becomes a property of the schema instead of a rule the engine
 *    has to remember, and the caller's key is not consulted at all — there is
 *    no second address to consult it about.
 *  - **No index is declared `unique`.** `duplicateIdentity` is a fail-closed
 *    *read* trigger: two records claiming one identity must be detectable and
 *    reportable so the app can stop and say so. A unique index would instead
 *    throw at write time, where a caller might swallow it, and the user would
 *    never learn which two records collided.
 */

import { STORE_IDS } from "@ff/domain";
import type { StoreId } from "@ff/domain";
import type { RecordKey } from "@ff/domain/ports";

/**
 * How a store addresses its records.
 *
 * `inline` stores keep the key inside the row under `key`, so a record is
 * self-addressing and a row copied between databases keeps its identity.
 */
export type RecordKeying =
  | { readonly kind: "inline"; readonly keyPath: "key" }
  | { readonly kind: "singleton"; readonly key: RecordKey };

/**
 * A materialized index. `keyPath` addresses the `index.*` scalars copied out of
 * the payload at write time, so a query filters without decoding — and because
 * they are derived from the same value in the same step, an index can never
 * describe a payload it was not derived from.
 */
export interface RecordIndexDescriptor {
  readonly name: string;
  readonly keyPath: string;
  /** True only for `journalEntries.index.tags`, which holds an array. */
  readonly multiEntry: boolean;
}

export interface RecordStoreLayout {
  readonly store: StoreId;
  readonly keying: RecordKeying;
  readonly indexes: readonly RecordIndexDescriptor[];
}

const INLINE: RecordKeying = { kind: "inline", keyPath: "key" };

function singleton(key: RecordKey): RecordKeying {
  return { kind: "singleton", key };
}

function index(field: string, multiEntry = false): RecordIndexDescriptor {
  // Name and key path are the same string on purpose: an index whose name did
  // not say which field it reads is an invitation to add a second index on the
  // same field under a different name.
  return { name: `index.${field}`, keyPath: `index.${field}`, multiEntry };
}

/**
 * The literal key both active-checkpoint stores use. They are separate stores,
 * so sharing the word costs nothing and says the same thing in both places:
 * there is one active record, and this is where it lives.
 */
export const ACTIVE_RECORD_KEY = "active";

export const RECORD_STORE_LAYOUTS: readonly RecordStoreLayout[] = [
  {
    store: STORE_IDS.activeStructuredCheckpoint,
    keying: singleton(ACTIVE_RECORD_KEY),
    indexes: [],
  },
  {
    store: STORE_IDS.activeOpenEffortCheckpoint,
    keying: singleton(ACTIVE_RECORD_KEY),
    indexes: [],
  },
  {
    store: STORE_IDS.canonicalEffortSessions,
    keying: INLINE,
    indexes: [index("startedAt"), index("outcome"), index("planId")],
  },
  {
    store: STORE_IDS.legacyWorkoutRecords,
    keying: INLINE,
    indexes: [index("startedAt")],
  },
  {
    store: STORE_IDS.savedPlans,
    keying: INLINE,
    indexes: [index("name"), index("mode")],
  },
  {
    /**
     * `PersistedComboScore` carries no payload version and treats every field
     * as optional, so the only useful materialized scalars are the two a saved
     * combo is ever ordered or filtered by: when it was saved, and which stance
     * it was authored in.
     */
    store: STORE_IDS.savedCombos,
    keying: INLINE,
    indexes: [index("createdAt"), index("stance")],
  },
  {
    store: STORE_IDS.sessionReflections,
    keying: INLINE,
    indexes: [index("updatedAt")],
  },
  {
    store: STORE_IDS.journalEntries,
    keying: INLINE,
    indexes: [
      index("occurredAt"),
      index("createdAt"),
      index("kind"),
      // Tags are folded, deduplicated and sorted on save but preserved
      // byte-exact on decode: the index is derived, never written back into the
      // payload.
      index("tags", true),
      index("effortSessionID"),
    ],
  },
  {
    // The draft's key is its entry's id, so a draft and its entry collide by
    // construction and promotion is a two-store transaction rather than an id
    // reconciliation.
    store: STORE_IDS.journalDrafts,
    keying: INLINE,
    indexes: [index("savedAt")],
  },
  {
    store: STORE_IDS.journalGoals,
    keying: INLINE,
    indexes: [index("status"), index("updatedAt")],
  },
  {
    store: STORE_IDS.settings,
    keying: singleton("settings"),
    indexes: [],
  },
];

export function recordStoreLayout(store: StoreId): RecordStoreLayout | undefined {
  for (const layout of RECORD_STORE_LAYOUTS) {
    if (layout.store === store) return layout;
  }
  return undefined;
}

/**
 * The key a mutation actually lands on.
 *
 * For a singleton store the layout's literal wins over whatever the caller
 * passed. That is the enforcement half of the schema guarantee: if a caller's
 * key were honoured, an out-of-line `put` under a second key would create a
 * second active checkpoint, which is the one thing this store shape exists to
 * make impossible.
 */
export function resolveRecordKey(layout: RecordStoreLayout, key: RecordKey): RecordKey {
  return layout.keying.kind === "singleton" ? layout.keying.key : key;
}
